import { FC, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Divider,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { Owner, Trainer, clientApi, Client, Horse } from '../utils/api';
import { barnsService } from '../services/barnsService';
import { useAnalytics } from '../hooks/useAnalytics';
import { dobFromApproxAge, approxAgeFromDob } from '../utils/age';
import {
  withOfflineFallback,
  readClientsPage,
  readDistinctLocations,
  executeWrite,
  executeUpdate,
  OfflineWriteBlockedError,
} from '../offline';
import { useSnackbar } from 'notistack';
import PhotoUploader from './PhotoUploader';
import { todayLocalISO } from '../utils/dateUtils';

/**
 * Shared Add/Edit Patient dialog — extracted verbatim from the PatientList
 * page so the chart views (Classic + Modern) can offer an in-place Edit
 * button without duplicating the ~500-line form.
 *
 * Contract:
 * - `horse: null | undefined` ⇒ create mode; a `Horse` ⇒ edit mode.
 * - Saves route through the offline op-queue (executeWrite/executeUpdate),
 *   exactly like the original — offline edits queue, blocked writes surface
 *   a snackbar.
 * - On success the component invalidates `['horses']` and, in edit mode,
 *   `['horse', id]` (the chart query key in both profile views), so every
 *   caller gets a fresh row without wiring its own invalidation.
 */

export interface HorseFormDialogProps {
  open: boolean;
  /** null/undefined = create mode. */
  horse?: Horse | null;
  onClose: () => void;
  /** Fires after a successful create OR update, with the saved horse. */
  onSaved?: (horse: Horse) => void;
}

const initialHorseData = {
  species: 'Equine',
  name: '',
  breed: '',
  secondBreed: '',
  isMixed: false,
  dateOfBirth: todayLocalISO(),
  gender: '',
  color: '',
  registeredName: '',
  microchipId: '',
  registrationNumber: '',
  discipline: '',
  insuranceCarrier: '',
  insurancePolicyNumber: '',
  status: 'ACTIVE' as 'ACTIVE' | 'INACTIVE' | 'DECEASED',
  dateOfDeath: '' as string,
  causeOfDeath: '' as string,
  owners: [{ name: '', email: '', phone: '', ownershipPercentage: 100 }] as Owner[],
  trainers: [] as Trainer[],
  location: '',
  peiNumber: '',
  weight: undefined as number | undefined,
  height: undefined as number | undefined,
};

type HorseFormData = typeof initialHorseData;

/** Map an existing horse row onto the form shape (edit mode). */
function horseToForm(horse: Horse | null | undefined): HorseFormData {
  if (!horse) return initialHorseData;
  return {
    species: horse.species || 'Equine',
    name: horse.name,
    breed: horse.breed,
    secondBreed: horse.secondBreed || '',
    isMixed: horse.isMixed ?? false,
    dateOfBirth: new Date(horse.dateOfBirth).toISOString().split('T')[0],
    gender: horse.gender,
    color: horse.color,
    registeredName: horse.registeredName || '',
    microchipId: horse.microchipId || '',
    registrationNumber: horse.registrationNumber || '',
    discipline: horse.discipline || '',
    insuranceCarrier: horse.insuranceCarrier || '',
    insurancePolicyNumber: horse.insurancePolicyNumber || '',
    status: horse.status || 'ACTIVE',
    dateOfDeath: horse.dateOfDeath ? horse.dateOfDeath.split('T')[0] : '',
    causeOfDeath: horse.causeOfDeath || '',
    owners:
      horse.owners && horse.owners.length > 0
        ? horse.owners
        : [{ name: '', email: '', phone: '', ownershipPercentage: 100 }],
    trainers: horse.trainers && horse.trainers.length > 0 ? horse.trainers : [],
    location: horse.location || '',
    peiNumber: horse.peiNumber || '',
    weight: horse.weight,
    height: horse.height,
  };
}

const distributePercentagesEqually = (owners: Owner[]): Owner[] => {
  const equalShare = Math.round((100 / owners.length) * 100) / 100;
  return owners.map((o, i) => ({
    ...o,
    ownershipPercentage:
      i === owners.length - 1
        ? Math.round((100 - equalShare * (owners.length - 1)) * 100) / 100
        : equalShare,
  }));
};

const BREED_OPTIONS = [
  'Thoroughbred',
  'Quarter Horse',
  'Arabian',
  'Paint',
  'Appaloosa',
  'Warmblood',
  'Morgan',
  'Tennessee Walking Horse',
  'Standardbred',
  'Mustang',
  'Andalusian',
  'Friesian',
  'Clydesdale',
  'Percheron',
  'Belgian',
  'Shetland Pony',
  'Welsh Pony',
  'Miniature Horse',
  'Mixed Breed',
  'Other',
];

/**
 * Inner content — mounted only while the dialog is open and re-keyed by
 * horse id, so form state hydrates synchronously in the useState initializer
 * (no flash of defaults, no stale form when switching horses).
 */
const HorseFormDialogContent: FC<{
  horse: Horse | null;
  onClose: () => void;
  onSaved?: (horse: Horse) => void;
}> = ({ horse, onClose, onSaved }) => {
  const { trackFeatureUsed } = useAnalytics();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [horseFormData, setHorseFormData] = useState<HorseFormData>(() => horseToForm(horse));
  const [ownerSearchTerms, setOwnerSearchTerms] = useState<{ [key: number]: string }>({});

  // Clients used by the owner autocomplete. Offline-aware so the owner
  // suggestions populate from the replica.
  const { data: clients = [] } = useQuery<Client[]>(
    ['clients-autocomplete'],
    withOfflineFallback(
      () => clientApi.list(),
      () => readClientsPage({ page: 1, limit: 1000 }).then((p) => p.data as Client[]),
    ),
    { staleTime: 30_000 },
  );

  // Active Barn records (canonical names), so the patient form offers them as
  // pick-from-list options instead of free text (prevents "Willowbrook" vs
  // "Willowbrook Stables" drift). Free-typing a new name is still allowed —
  // the backend find-or-creates a Barn and links Horse.barnId (dual-write).
  const { data: barnOptions = [] } = useQuery<string[]>(
    ['barns', 'names'],
    withOfflineFallback(
      () => barnsService.list().then((barns) => barns.map((b) => b.name)),
      () => readDistinctLocations() as any,
    ),
    { staleTime: 5 * 60_000 },
  );

  const addHorse = useMutation(
    async (data: HorseFormData) =>
      executeWrite<Horse>({
        entityType: 'horse',
        table: 'horses',
        payload: data,
        networkFn: () => api.post('/horses', data).then((r) => r.data),
      }),
    {
      onSuccess: (saved) => {
        queryClient.invalidateQueries(['horses']);
        if (saved) onSaved?.(saved as Horse);
        onClose();
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error?.response?.data?.message || 'Failed to save patient', { variant: 'error' });
      },
    },
  );

  const updateHorse = useMutation(
    async ({ id, data }: { id: string; data: Partial<Horse> }) =>
      executeUpdate<Horse>({
        entityType: 'horse',
        table: 'horses',
        entityId: id,
        payload: data,
        networkFn: () => api.put(`/horses/${id}`, data).then((r) => r.data),
      }),
    {
      onSuccess: (saved, variables) => {
        trackFeatureUsed('horse_updated');
        queryClient.invalidateQueries(['horses']);
        // The chart views (Classic + Modern) read ['horse', id].
        queryClient.invalidateQueries(['horse', variables.id]);
        if (saved) onSaved?.(saved as Horse);
        onClose();
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          enqueueSnackbar(error.message, { variant: 'error' });
          return;
        }
        enqueueSnackbar(error?.response?.data?.message || 'Failed to update patient', { variant: 'error' });
      },
    },
  );

  const isSaving = addHorse.isLoading || updateHorse.isLoading;

  const handleAddOwner = () => {
    if (horseFormData.owners.length < 5) {
      const newOwners = [...horseFormData.owners, { name: '', email: '', phone: '', ownershipPercentage: 0 }];
      setHorseFormData({ ...horseFormData, owners: distributePercentagesEqually(newOwners) });
    }
  };

  const handleRemoveOwner = (index: number) => {
    if (horseFormData.owners.length > 1) {
      const newOwners = horseFormData.owners.filter((_, i) => i !== index);
      setHorseFormData({ ...horseFormData, owners: distributePercentagesEqually(newOwners) });
    }
  };

  const handleOwnerChange = (index: number, field: keyof Owner, value: string | number) => {
    const newOwners = [...horseFormData.owners];
    newOwners[index] = { ...newOwners[index], [field]: value };
    setHorseFormData({ ...horseFormData, owners: newOwners });
  };

  const handleAddTrainer = () => {
    if (horseFormData.trainers.length < 5) {
      setHorseFormData({
        ...horseFormData,
        trainers: [...horseFormData.trainers, { name: '', email: '', phone: '' }],
      });
    }
  };

  const handleRemoveTrainer = (index: number) => {
    const newTrainers = horseFormData.trainers.filter((_, i) => i !== index);
    setHorseFormData({ ...horseFormData, trainers: newTrainers });
  };

  const handleTrainerChange = (index: number, field: keyof Trainer, value: string) => {
    const newTrainers = [...horseFormData.trainers];
    newTrainers[index] = { ...newTrainers[index], [field]: value };
    setHorseFormData({ ...horseFormData, trainers: newTrainers });
  };

  const handleSaveHorse = () => {
    const formattedData = {
      ...horseFormData,
      dateOfBirth: new Date(horseFormData.dateOfBirth).toISOString(),
      // Status is driven explicitly by the Status select (which clears the
      // death fields when it leaves DECEASED), so send it through as-is — the
      // backend still auto-flips on create when no explicit status is given.
    };
    if (horse) {
      updateHorse.mutate({ id: horse.id, data: formattedData });
    } else {
      addHorse.mutate(formattedData);
    }
  };

  return (
    <>
      <DialogTitle>{horse ? 'Edit Patient' : 'Add New Patient'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={2}>
          <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
            <PhotoUploader
              horseId={horse?.id}
              caption={horse ? 'Patient photo' : 'Photo can be added after saving'}
            />
          </Box>
          <TextField
            label="Name"
            value={horseFormData.name}
            onChange={(e) => setHorseFormData({ ...horseFormData, name: e.target.value })}
            fullWidth
            required
          />

          <TextField
            label="Registered Name"
            value={horseFormData.registeredName}
            onChange={(e) => setHorseFormData({ ...horseFormData, registeredName: e.target.value })}
            fullWidth
            placeholder="Formal registered name, e.g. Dances With Fire"
          />

          <FormControl fullWidth required>
            <InputLabel>Species</InputLabel>
            <Select
              value={horseFormData.species}
              onChange={(e) => {
                const newSpecies = e.target.value;
                if (newSpecies === 'Other') {
                  setHorseFormData({
                    ...horseFormData,
                    species: newSpecies,
                    breed: 'Other',
                    gender: 'Other',
                  });
                } else {
                  setHorseFormData({
                    ...horseFormData,
                    species: newSpecies,
                    breed: '',
                    gender: '',
                  });
                }
              }}
              label="Species"
            >
              <MenuItem value="Equine">Equine</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            freeSolo
            disabled={horseFormData.species === 'Other'}
            options={horseFormData.species === 'Equine' ? BREED_OPTIONS : []}
            value={horseFormData.breed}
            onChange={(_event: any, newValue: string | null) => {
              setHorseFormData({ ...horseFormData, breed: newValue || '' });
            }}
            onInputChange={(_event: any, newInputValue: string) => {
              setHorseFormData({ ...horseFormData, breed: newInputValue });
            }}
            renderInput={(params: any) => (
              <TextField {...params} label="Breed" required fullWidth />
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={horseFormData.isMixed}
                onChange={(e) =>
                  setHorseFormData({
                    ...horseFormData,
                    isMixed: e.target.checked,
                    secondBreed: e.target.checked ? horseFormData.secondBreed : '',
                  })
                }
              />
            }
            label="Mixed breed"
          />

          {horseFormData.isMixed && (
            <Autocomplete
              freeSolo
              disabled={horseFormData.species === 'Other'}
              options={horseFormData.species === 'Equine' ? BREED_OPTIONS.filter((b) => b !== 'Mixed Breed') : []}
              value={horseFormData.secondBreed}
              onChange={(_event: any, newValue: string | null) => {
                setHorseFormData({ ...horseFormData, secondBreed: newValue || '' });
              }}
              onInputChange={(_event: any, newInputValue: string) => {
                setHorseFormData({ ...horseFormData, secondBreed: newInputValue });
              }}
              renderInput={(params: any) => (
                <TextField {...params} label="Second Breed" fullWidth />
              )}
            />
          )}

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              type="date"
              label="Date of Birth"
              value={horseFormData.dateOfBirth}
              onChange={(e) => setHorseFormData({ ...horseFormData, dateOfBirth: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            {/* Approximate-age shortcut: many ambulatory clients only know the
                horse's age, not its DOB. Typing an age sets DOB to Jan 1 of
                that birth year; storage stays the DOB date string. */}
            <TextField
              type="number"
              label="Age (yrs)"
              value={approxAgeFromDob(horseFormData.dateOfBirth)}
              onChange={(e) => {
                const dob = dobFromApproxAge(e.target.value);
                if (dob) setHorseFormData({ ...horseFormData, dateOfBirth: dob });
              }}
              helperText="Sets DOB to Jan 1"
              sx={{ width: 150, flexShrink: 0 }}
              inputProps={{ min: 0, max: 99 }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <FormControl fullWidth required>
            <InputLabel>Gender</InputLabel>
            <Select
              value={horseFormData.gender}
              onChange={(e) => setHorseFormData({ ...horseFormData, gender: e.target.value })}
              label="Gender"
              disabled={horseFormData.species === 'Other'}
            >
              {horseFormData.species === 'Equine' && <MenuItem value="Mare">Mare</MenuItem>}
              {horseFormData.species === 'Equine' && <MenuItem value="Gelding">Gelding</MenuItem>}
              {horseFormData.species === 'Equine' && <MenuItem value="Colt">Colt</MenuItem>}
              {horseFormData.species === 'Equine' && <MenuItem value="Filly">Filly</MenuItem>}
              {horseFormData.species === 'Equine' && <MenuItem value="Stallion">Stallion</MenuItem>}
              {horseFormData.species === 'Equine' && <MenuItem value="Cryptorchid">Cryptorchid</MenuItem>}
              {horseFormData.species === 'Equine' && <MenuItem value="Ridgling">Ridgling</MenuItem>}
              {horseFormData.species === 'Other' && <MenuItem value="Other">Other</MenuItem>}
            </Select>
          </FormControl>

          <TextField
            label="Color"
            value={horseFormData.color}
            onChange={(e) => setHorseFormData({ ...horseFormData, color: e.target.value })}
            fullWidth
            required
          />

          <TextField
            label="Microchip ID"
            value={horseFormData.microchipId}
            onChange={(e) => setHorseFormData({ ...horseFormData, microchipId: e.target.value })}
            fullWidth
          />

          <TextField
            label="Registration Number"
            value={horseFormData.registrationNumber}
            onChange={(e) => setHorseFormData({ ...horseFormData, registrationNumber: e.target.value })}
            fullWidth
            placeholder="Breed registry or competition number"
          />

          <TextField
            label="Discipline"
            value={horseFormData.discipline}
            onChange={(e) => setHorseFormData({ ...horseFormData, discipline: e.target.value })}
            fullWidth
            placeholder="e.g. Hunter/Jumper, Dressage"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Insurance Carrier"
              value={horseFormData.insuranceCarrier}
              onChange={(e) => setHorseFormData({ ...horseFormData, insuranceCarrier: e.target.value })}
              fullWidth
            />
            <TextField
              label="Policy Number"
              value={horseFormData.insurancePolicyNumber}
              onChange={(e) => setHorseFormData({ ...horseFormData, insurancePolicyNumber: e.target.value })}
              fullWidth
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={horseFormData.status}
              onChange={(e) => {
                const status = e.target.value as 'ACTIVE' | 'INACTIVE' | 'DECEASED';
                setHorseFormData({
                  ...horseFormData,
                  status,
                  // Leaving the deceased state clears the death fields so we
                  // never persist an ACTIVE/INACTIVE horse with an orphaned
                  // date of death — and so un-deceasing actually sticks.
                  ...(status !== 'DECEASED' ? { dateOfDeath: '', causeOfDeath: '' } : {}),
                });
              }}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
              <MenuItem value="DECEASED">Deceased</MenuItem>
            </Select>
          </FormControl>

          {horseFormData.status === 'DECEASED' && (
            <>
              <TextField
                type="date"
                label="Date of Death (optional)"
                value={horseFormData.dateOfDeath}
                onChange={(e) => setHorseFormData({ ...horseFormData, dateOfDeath: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Cause of Death (optional)"
                value={horseFormData.causeOfDeath}
                onChange={(e) => setHorseFormData({ ...horseFormData, causeOfDeath: e.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
            </>
          )}

          <Autocomplete
            freeSolo
            options={barnOptions}
            value={horseFormData.location}
            onChange={(_event: any, newValue: string | null) => {
              setHorseFormData({ ...horseFormData, location: newValue || '' });
            }}
            onInputChange={(_event: any, newInputValue: string) => {
              setHorseFormData({ ...horseFormData, location: newInputValue });
            }}
            renderInput={(params: any) => (
              <TextField
                {...params}
                label="Barn / Location (optional)"
                fullWidth
                placeholder="Pick a barn or type a new one"
              />
            )}
          />

          <TextField
            label="PEI Number"
            value={horseFormData.peiNumber}
            onChange={(e) => setHorseFormData({ ...horseFormData, peiNumber: e.target.value })}
            fullWidth
            placeholder="Permanent Equine Identification number"
          />

          <TextField
            label="Weight (kg)"
            type="number"
            value={horseFormData.weight || ''}
            onChange={(e) =>
              setHorseFormData({ ...horseFormData, weight: e.target.value ? parseFloat(e.target.value) : undefined })
            }
            fullWidth
            placeholder="e.g., 476"
            inputProps={{ min: 0, step: 0.1 }}
          />

          <TextField
            label={horseFormData.species === 'Equine' ? 'Height (hh)' : 'Height (optional)'}
            type="number"
            value={horseFormData.height || ''}
            onChange={(e) =>
              setHorseFormData({ ...horseFormData, height: e.target.value ? parseFloat(e.target.value) : undefined })
            }
            fullWidth
            placeholder={horseFormData.species === 'Equine' ? 'e.g., 15.2' : 'e.g., 24'}
            inputProps={{ min: 0, step: 0.1 }}
            helperText={horseFormData.species === 'Equine' ? 'Hands' : 'Inches or cm'}
          />

          <Divider sx={{ my: 2 }} />

          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Owners</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddOwner}
                disabled={horseFormData.owners.length >= 5}
                size="small"
              >
                Add Owner ({horseFormData.owners.length}/5)
              </Button>
            </Box>

            {horseFormData.owners.map((owner, index) => (
              <Box key={index} mb={2} p={2} border="1px solid #e0e0e0" borderRadius={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">
                    {index === 0 ? 'Main Owner (Required)' : `Owner ${index + 1}`}
                  </Typography>
                  {index > 0 && (
                    <IconButton size="small" color="error" onClick={() => handleRemoveOwner(index)}>
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Autocomplete
                    freeSolo
                    options={clients}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                    inputValue={ownerSearchTerms[index] !== undefined ? ownerSearchTerms[index] : owner.name}
                    onInputChange={(_, newValue) => {
                      setOwnerSearchTerms({ ...ownerSearchTerms, [index]: newValue });
                      handleOwnerChange(index, 'name', newValue);
                    }}
                    onChange={(_, value) => {
                      if (value && typeof value !== 'string') {
                        const updatedOwners = [...horseFormData.owners];
                        updatedOwners[index] = {
                          ...updatedOwners[index],
                          name: value.name,
                          email: value.email || '',
                          phone: value.phone || '',
                        };
                        setHorseFormData({ ...horseFormData, owners: updatedOwners });
                        setOwnerSearchTerms({ ...ownerSearchTerms, [index]: value.name });
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={typeof option === 'string' ? option : option.id}>
                        {typeof option === 'string' ? (
                          option
                        ) : (
                          <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            {option.email && (
                              <Typography variant="caption" color="text.secondary">
                                {option.email}
                              </Typography>
                            )}
                            {option.phone && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: option.email ? 1 : 0 }}
                              >
                                {option.phone}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Name"
                        required
                        fullWidth
                        placeholder="Start typing to search existing clients..."
                      />
                    )}
                  />
                  <Box display="flex" gap={2}>
                    <TextField
                      label="Email"
                      type="email"
                      value={owner.email || ''}
                      onChange={(e) => handleOwnerChange(index, 'email', e.target.value)}
                      fullWidth
                      required={index === 0}
                    />
                    <TextField
                      label="Phone"
                      value={owner.phone || ''}
                      onChange={(e) => handleOwnerChange(index, 'phone', e.target.value)}
                      fullWidth
                      required={index === 0}
                    />
                  </Box>
                  {horseFormData.owners.length > 1 && (
                    <TextField
                      label="Ownership %"
                      type="number"
                      value={owner.ownershipPercentage ?? ''}
                      onChange={(e) =>
                        handleOwnerChange(
                          index,
                          'ownershipPercentage',
                          e.target.value ? parseFloat(e.target.value) : 0,
                        )
                      }
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      size="small"
                      sx={{ maxWidth: 150 }}
                      helperText="Billing split"
                    />
                  )}
                </Box>
              </Box>
            ))}
            {horseFormData.owners.length > 1 &&
              (() => {
                const totalPct = horseFormData.owners.reduce((sum, o) => sum + (o.ownershipPercentage ?? 0), 0);
                const isValid = Math.abs(totalPct - 100) < 0.02;
                return (
                  <Typography
                    variant="body2"
                    sx={{ mt: -1, mb: 1, color: isValid ? 'success.main' : 'error.main', fontWeight: 500 }}
                  >
                    Total ownership: {totalPct.toFixed(1)}%{!isValid && ' (must equal 100%)'}
                  </Typography>
                );
              })()}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Trainers (Optional)</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTrainer}
                disabled={horseFormData.trainers.length >= 5}
                size="small"
              >
                Add Trainer ({horseFormData.trainers.length}/5)
              </Button>
            </Box>

            {horseFormData.trainers.map((trainer, index) => (
              <Box key={index} mb={2} p={2} border="1px solid #e0e0e0" borderRadius={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Trainer {index + 1}</Typography>
                  <IconButton size="small" color="error" onClick={() => handleRemoveTrainer(index)}>
                    <RemoveIcon />
                  </IconButton>
                </Box>
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Name"
                    value={trainer.name}
                    onChange={(e) => handleTrainerChange(index, 'name', e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={trainer.email || ''}
                    onChange={(e) => handleTrainerChange(index, 'email', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Phone"
                    value={trainer.phone || ''}
                    onChange={(e) => handleTrainerChange(index, 'phone', e.target.value)}
                    fullWidth
                  />
                </Box>
              </Box>
            ))}

            {horseFormData.trainers.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}
              >
                No trainers added. Click "Add Trainer" to add one.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSaveHorse}
          disabled={
            isSaving ||
            !horseFormData.name ||
            !horseFormData.breed ||
            !horseFormData.dateOfBirth ||
            !horseFormData.gender ||
            !horseFormData.color ||
            !horseFormData.owners[0]?.name ||
            !horseFormData.owners[0]?.email ||
            !horseFormData.owners[0]?.phone ||
            horseFormData.owners.some((owner) => !owner.name)
          }
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
};

const HorseFormDialog: FC<HorseFormDialogProps> = ({ open, horse = null, onClose, onSaved }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' },
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      {open && (
        <HorseFormDialogContent
          key={horse?.id ?? 'new'}
          horse={horse}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    </Dialog>
  );
};

export default HorseFormDialog;
